import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import { CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT } from '../types/crm-integration-chat.types'
import type { CrmIntegrationMessageView } from '../types/crm-integration-message.types'
import {
  mergeCrmIntegrationMessagesPage,
  sortCrmIntegrationMessagesAsc,
} from '../lib/crmIntegrationMessagesMerge'
import { parseCrmIntegrationMessagesPayload } from '../lib/parseCrmIntegrationMessagesResponse'

type ScrollRestoreSnapshot = {
  scrollTop: number
  scrollHeight: number
}

const applyPrependScrollRestore = (
  viewport: HTMLDivElement,
  snapshot: ScrollRestoreSnapshot,
): boolean => {
  const heightDiff = viewport.scrollHeight - snapshot.scrollHeight
  if (heightDiff <= 0) {
    return false
  }

  const previousScrollBehavior = viewport.style.scrollBehavior
  viewport.style.scrollBehavior = 'auto'
  viewport.scrollTop = snapshot.scrollTop + heightDiff
  viewport.style.scrollBehavior = previousScrollBehavior

  return true
}

export type UseCrmIntegrationLoadOlderMessagesParams = {
  conversationId: number | null
  setServerMessages: Dispatch<SetStateAction<CrmIntegrationMessageView[]>>
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>
}

export type UseCrmIntegrationLoadOlderMessagesResult = {
  loadOlderMessages: () => Promise<void>
  isLoadingOlder: boolean
  hasMoreOlder: boolean
  resetOlderPagination: () => void
  markInitialPageLoaded: (messageCount: number) => void
}

export const useCrmIntegrationLoadOlderMessages = ({
  conversationId,
  setServerMessages,
  scrollContainerRef,
}: UseCrmIntegrationLoadOlderMessagesParams): UseCrmIntegrationLoadOlderMessagesResult => {
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  const lastLoadedPageRef = useRef(0)
  const [isLoadingOlder, setIsLoadingOlder] = useState(false)
  const [hasMoreOlder, setHasMoreOlder] = useState(true)

  const pendingScrollRestoreRef = useRef<ScrollRestoreSnapshot | null>(null)
  const [scrollRestoreKey, setScrollRestoreKey] = useState(0)

  const syncLastLoadedPage = useCallback((page: number) => {
    lastLoadedPageRef.current = page
  }, [])

  const resetOlderPagination = useCallback(() => {
    syncLastLoadedPage(0)
    setHasMoreOlder(true)
    setIsLoadingOlder(false)
    pendingScrollRestoreRef.current = null
  }, [syncLastLoadedPage])

  const markInitialPageLoaded = useCallback(
    (messageCount: number) => {
      syncLastLoadedPage(1)
      setHasMoreOlder(messageCount >= CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT)
    },
    [syncLastLoadedPage],
  )

  useLayoutEffect(() => {
    const snapshot = pendingScrollRestoreRef.current
    const viewport = scrollContainerRef.current

    if (!snapshot || !viewport) {
      return
    }

    const finish = () => {
      pendingScrollRestoreRef.current = null
    }

    if (applyPrependScrollRestore(viewport, snapshot)) {
      finish()
      return
    }

    requestAnimationFrame(() => {
      const current = pendingScrollRestoreRef.current
      if (!current || !scrollContainerRef.current) {
        finish()
        return
      }

      if (applyPrependScrollRestore(scrollContainerRef.current, current)) {
        finish()
        return
      }

      window.setTimeout(() => {
        const snap = pendingScrollRestoreRef.current
        const el = scrollContainerRef.current
        if (snap && el) {
          applyPrependScrollRestore(el, snap)
        }
        finish()
      }, 0)
    })
  }, [scrollContainerRef, scrollRestoreKey])

  const loadOlderMessages = useCallback(async () => {
    if (isLoadingOlder || !hasMoreOlder) {
      return
    }

    const viewport = scrollContainerRef.current
    if (viewport) {
      pendingScrollRestoreRef.current = {
        scrollTop: viewport.scrollTop,
        scrollHeight: viewport.scrollHeight,
      }
    }

    const currentConversationId = conversationIdRef.current
    const offset = lastLoadedPageRef.current * CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT

    if (currentConversationId == null) {
      pendingScrollRestoreRef.current = null
      return
    }

    setIsLoadingOlder(true)

    try {
      const res = await crmIntegrationChatApi.getIntegrationMessages({
        conversation_id: currentConversationId,
        limit: CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT,
        offset,
      })

      if (isError(res)) {
        pendingScrollRestoreRef.current = null
        return
      }

      const payload = res.data as ResponseWrapper<unknown>
      const parsed = parseCrmIntegrationMessagesPayload(payload?.data)
      const olderPage = sortCrmIntegrationMessagesAsc(parsed.items)

      setServerMessages((prev) => mergeCrmIntegrationMessagesPage(prev, olderPage))
      syncLastLoadedPage(lastLoadedPageRef.current + 1)
      setHasMoreOlder(parsed.items.length >= CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT)
      setScrollRestoreKey((key) => key + 1)
    } catch {
      pendingScrollRestoreRef.current = null
    } finally {
      setIsLoadingOlder(false)
    }
  }, [
    hasMoreOlder,
    isLoadingOlder,
    scrollContainerRef,
    setServerMessages,
    syncLastLoadedPage,
  ])

  return {
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
    resetOlderPagination,
    markInitialPageLoaded,
  }
}
